/**
 * typographyProps.js
 * ──────────────────
 * Factory for generating a standard set of Vue 2 typography prop definitions.
 *
 * The complete "typography control" in the CMS sidebar exposes these fields:
 *   fontFamily, fontSize, fontWeight, fontStyle,
 *   lineHeight, letterSpacing, color,
 *   textTransform, textDecoration
 *
 * Use `createTypographyProps()` when defining block props to ensure every
 * block uses consistent prop names and defaults.  Pair it with
 * `buildTypographyStyle()` from `blockUtils.js` to compute the style object.
 *
 * DB-COMPATIBILITY: The generated prop names become JSON keys stored in the
 * database.  Never rename a key without a migration strategy — just add new
 * ones alongside the old ones instead.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * Usage — single text element (no prefix):
 *
 *   import { createTypographyProps } from '@blocks/shared/typographyProps.js';
 *
 *   props: Object.assign({}, createTypographyProps(), {
 *     text: { type: String, default: '' }
 *   })
 *
 * Usage — block that styles multiple text elements (prefixed):
 *
 *   import { createTypographyProps } from '@blocks/shared/typographyProps.js';
 *
 *   props: Object.assign(
 *     {},
 *     createTypographyProps('title',   { fontSize: '1.1rem' }),
 *     createTypographyProps('excerpt', { fontSize: '0.875rem' }),
 *     { otherProp: { type: String, default: '' } }
 *   )
 *
 * In the above, 'title' generates: titleFontFamily, titleFontSize, …
 * ─────────────────────────────────────────────────────────────────────────────
 */

/**
 * The canonical list of typography field base-names (without prefix).
 * These are the keys recognised by `buildTypographyStyle()` in blockUtils.js.
 */
export var TYPOGRAPHY_FIELD_NAMES = [
	'fontFamily',
	'fontSize',
	'fontWeight',
	'fontStyle',
	'lineHeight',
	'letterSpacing',
	'color',
	'textTransform',
	'textDecoration'
];

import { capitalize } from './blockUtils.js';

/**
 * Generate a Vue 2 props definition fragment for the standard typography fields.
 *
 * @param {string}  [prefix='']   Optional camelCase prefix applied to every
 *                                prop name.  e.g. 'title' → titleFontFamily.
 *                                When omitted (or '') props are unprefixed
 *                                and use camelCase: fontFamily, fontSize, etc.
 * @param {object}  [defaults={}] Default value overrides keyed by the BASE
 *                                field name (without prefix), e.g. { fontSize: '1rem' }.
 * @returns {object} Vue props definition fragment ready to be spread into
 *                   a component's `props` option.
 */
export function createTypographyProps(prefix, defaults) {
	var d = defaults || {};
	var result = {};

	for (var i = 0; i < TYPOGRAPHY_FIELD_NAMES.length; i++) {
		var key = TYPOGRAPHY_FIELD_NAMES[i];
		var propName = prefix ? (prefix + capitalize(key)) : key;
		result[propName] = { type: String, default: d[key] !== undefined ? d[key] : '' };
	}

	return result;
}
